import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getObservations,
  getObservationById,
  createObservation,
  updateObservation,
  deleteObservation,
  getObservationStatusSummary,
  getObservationTypes,
  createObservationType,
  uploadObservationAttachment,
  linkObservationAttachmentPhoto,
  deleteObservationAttachment,
} from '../api/api';
import type {
  CreateObservationData,
  UpdateObservationData,
  ObservationFilters,
} from '../types';

export const observationKeys = {
  all: ['observations'] as const,
  lists: (projectId: number) => [...observationKeys.all, 'list', projectId] as const,
  list: (projectId: number, filters: ObservationFilters) =>
    [...observationKeys.lists(projectId), filters] as const,
  details: () => [...observationKeys.all, 'detail'] as const,
  detail: (id: string) => [...observationKeys.details(), id] as const,
  statusSummary: (projectId: number) =>
    [...observationKeys.all, 'status-summary', projectId] as const,
  types: () => [...observationKeys.all, 'types'] as const,
};

export const useObservations = (projectId: number | undefined, filters: ObservationFilters = {}) =>
  useQuery({
    queryKey: observationKeys.list(projectId!, filters),
    queryFn: () => getObservations(projectId!, filters),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useObservation = (id: string | undefined, projectId: number | undefined) =>
  useQuery({
    queryKey: observationKeys.detail(id!),
    queryFn: () => getObservationById(id!, projectId!),
    enabled: !!id && !!projectId,
    staleTime: 60_000,
  });

export const useObservationStatusSummary = (projectId: number | undefined) =>
  useQuery({
    queryKey: observationKeys.statusSummary(projectId!),
    queryFn: () => getObservationStatusSummary(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useObservationTypes = () =>
  useQuery({
    queryKey: observationKeys.types(),
    queryFn: getObservationTypes,
    staleTime: 5 * 60_000,
  });

export const useCreateObservationType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createObservationType(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: observationKeys.types() });
    },
  });
};

export const useObservationMutations = (projectId: number | undefined) => {
  const queryClient = useQueryClient();

  const invalidateLists = () => {
    if (!projectId) return;
    queryClient.invalidateQueries({ queryKey: observationKeys.lists(projectId) });
    queryClient.invalidateQueries({ queryKey: observationKeys.statusSummary(projectId) });
  };

  const createItem = useMutation({
    mutationFn: (data: CreateObservationData) => createObservation(data),
    onSuccess: () => invalidateLists(),
  });

  const updateItem = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateObservationData }) =>
      updateObservation(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: observationKeys.detail(result.id) });
      invalidateLists();
    },
  });

  const deleteItem = useMutation({
    mutationFn: (id: string) => deleteObservation(id),
    onSuccess: () => invalidateLists(),
  });

  const uploadAttachment = useMutation({
    mutationFn: ({ observationId, file }: { observationId: string; file: File }) =>
      uploadObservationAttachment(observationId, file),
    onSuccess: (_data, { observationId }) => {
      queryClient.invalidateQueries({ queryKey: observationKeys.detail(observationId) });
    },
  });

  const linkAttachmentPhoto = useMutation({
    mutationFn: ({ observationId, photoId }: { observationId: string; photoId: number }) =>
      linkObservationAttachmentPhoto(observationId, photoId),
    onSuccess: (_data, { observationId }) => {
      queryClient.invalidateQueries({ queryKey: observationKeys.detail(observationId) });
    },
  });

  const deleteAttachment = useMutation({
    mutationFn: ({ attachmentId, observationId }: { attachmentId: string; observationId: string }) =>
      deleteObservationAttachment(attachmentId).then(() => observationId),
    onSuccess: (observationId) => {
      queryClient.invalidateQueries({ queryKey: observationKeys.detail(observationId) });
    },
  });

  return { createItem, updateItem, deleteItem, uploadAttachment, linkAttachmentPhoto, deleteAttachment };
};
